import { Button, Group, Loader, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useState } from 'react';
import { z } from 'zod';

import { Transit, TransitStatus } from '@e-origin/shared';

import { useChangeTransitStatus } from '@admin/hooks';
import { Select } from '@components';

interface ChangeTransitStatusProps {
  onClose: () => void;
  transit: Transit;
}

const changeTransitStatusSchema = z.object({
  status: z.string(),
});

export const ChangeTransitStatus: React.FC<ChangeTransitStatusProps> = ({ onClose, transit }) => {
  const changeTransitStatusMutation = useChangeTransitStatus();
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      status: transit.generalInfo.status,
    },
    validate: zodResolver(changeTransitStatusSchema),
    validateInputOnBlur: true,
  });

  const statusOptions = Object.keys(TransitStatus).map((key) => ({
    value: TransitStatus[key],
    label: TransitStatus[key],
  }));

  const handleSubmit = async (values: typeof form.values) => {
    setFormError(null);
    try {
      await changeTransitStatusMutation.mutateAsync({
        status: values.status,
        transitId: transit._id,
      });
      onClose();
    } catch (error: any) {
      setFormError(error?.response?.data?.message || 'Failed to update transit status');
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md" mt="xl">
        <Select label="Status" placeholder="Select status" data={statusOptions} {...form.getInputProps('status')} searchable />

        {formError && (
          <Text c="red" fw="bold">
            {formError}
          </Text>
        )}
        <Group justify="center" mt="md">
          <Button type="button" onClick={onClose} disabled={changeTransitStatusMutation.isPending} variant="transparent">
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={changeTransitStatusMutation.isPending || !form.isValid()}
            leftSection={changeTransitStatusMutation.isPending && <Loader color="white" size="xs" />}
          >
            Change Status
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
